import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminStats from "@/components/AdminStats";
import AdminLeadTable from "@/components/AdminLeadTable";
import AdminProductSettings from "@/components/AdminProductSettings";
import AdminDiscountSettings from "@/components/AdminDiscountSettings";
import AdminEmailSettings from "@/components/AdminEmailSettings";
import AdminWhatsAppSettings from "@/components/AdminWhatsAppSettings";
import AdminLegalSettings from "@/components/AdminLegalSettings";
import AdminPenaltySettings from "@/components/AdminPenaltySettings";
import { Button } from "@/components/ui/button";
import { LogOut, Loader2, BarChart3, Package, Gift, Mail, MessageCircle, FileText, Scale } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({});
  const [adminEmail, setAdminEmail] = useState("");
  const [activeTab, setActiveTab] = useState("stats"); // "stats", "leads", "product"

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const email = localStorage.getItem("admin_email");

    if (!token) {
      toast.error("Lütfen giriş yapın");
      navigate("/admin/login");
      return;
    }

    setAdminEmail(email || "");
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");

    try {
      setLoading(true);

      const statsResponse = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(statsResponse.data);

      const leadsResponse = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters,
      });
      setLeads(leadsResponse.data.leads);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Oturum süreniz doldu. Lütfen tekrar giriş yapın.");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        navigate("/admin/login");
      } else {
        toast.error("Veri yüklenirken hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    toast.success("Çıkış yapıldı");
    navigate("/admin/login");
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      fetchData();
    }
  }, [filters]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen animated-gradient flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen animated-gradient">
      {/* Header */}
      <header className="border-b border-teal-500/20 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="logo-container">
                <div className="logo-ring"></div>
                <div className="logo-ring"></div>
                <img 
                  src="/images/alcodot-logo.png" 
                  alt="Alcodot Logo" 
                  className="logo-image h-10 w-auto"
                />
              </div>
              <div>
                <p className="text-sm text-teal-400 font-semibold">{adminEmail}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2 rounded-xl bg-red-500/10 border-2 border-red-500/30 text-red-400 hover:border-red-500 hover:bg-red-500/20 font-bold"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-teal-500/20 pb-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab("stats")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "stats"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-stats"
          >
            <BarChart3 className="w-5 h-5" />
            İstatistikler
          </button>
          <button
            onClick={() => setActiveTab("product")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "product"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-product"
          >
            <Package className="w-5 h-5" />
            Ürün
          </button>
          <button
            onClick={() => setActiveTab("discount")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "discount"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-discount"
          >
            <Gift className="w-5 h-5" />
            İndirim
          </button>
          <button
            onClick={() => setActiveTab("penalty")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "penalty"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-penalty"
          >
            <Scale className="w-5 h-5" />
            Cezalar
          </button>
          <button
            onClick={() => setActiveTab("email")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "email"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-email"
          >
            <Mail className="w-5 h-5" />
            Email
          </button>
          <button
            onClick={() => setActiveTab("whatsapp")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "whatsapp"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-whatsapp"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </button>
          <button
            onClick={() => setActiveTab("legal")}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
              activeTab === "legal"
                ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg"
                : "bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500"
            }`}
            data-testid="tab-legal"
          >
            <FileText className="w-5 h-5" />
            Yasal
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "stats" && (
          <>
            {stats && <AdminStats stats={stats} />}
            <AdminLeadTable
              leads={leads}
              onFilterChange={handleFilterChange}
              onRefresh={fetchData}
            />
          </>
        )}

        {activeTab === "product" && <AdminProductSettings />}
        
        {activeTab === "discount" && <AdminDiscountSettings />}
        
        {activeTab === "penalty" && <AdminPenaltySettings />}
        
        {activeTab === "email" && <AdminEmailSettings />}
        
        {activeTab === "whatsapp" && <AdminWhatsAppSettings />}
        
        {activeTab === "legal" && <AdminLegalSettings />}
      </main>
    </div>
  );
};

export default AdminDashboard;